import React, { useRef } from "react";

import { FormButton } from "components";

import { Form, Formik } from "formik";
import * as yup from "yup";

import { useRecoilValue } from "recoil";
import { userCredentials } from "recoilState/GlobalState";

import {
    CARACTERES_PERMITIDOS_MSG,
    CARACTERES_PERMITIDOS_REGEX,
} from "consts/validaciones";

import BloqueUbicacion, {
    ANCHOS_DOMICILIO_ANGOSTO_COMPACTO,
    CampoTexto,
    estilos,
    esSinNumero,
    reglasUbicacion,
    SIN_NUMERO,
    valoresUbicacionVacios,
} from "components/utils/mapa/BloqueUbicacion";
import { useUbicacion } from "components/utils/mapa/useUbicacion";
import PermisosDeReparticion, {
    PERMISOS_INICIALES,
} from "../components/PermisosDeReparticion";

/**
 * Formulario de establecimiento, compartido por las cinco entidades y por el alta y la
 * modificacion de cada una.
 *
 * Hospitales, CAPS, puestos, postas y URESA son la MISMA entidad del back: RNHEstablecimiento
 * con distinto rnhTipoComponenteCod. Tienen los mismos campos de identificacion (codigo y
 * nombre), el mismo domicilio y el mismo mapa; lo unico propio son los cuatro selects de
 * hospitales (nivel, zona, cabecera y area), que entran por `camposExtra`.
 *
 * El domicilio y el mapa viven en BloqueUbicacion. Aca queda el armado del schema, el guard de
 * la coordenada y los botones.
 */

export const LARGOS = { cod: 20, name: 45 };

const identificacion = (largo, mensajeRequerido) =>
    yup
        .string()
        .max(largo, `Debe tener menos de ${largo} caracteres`)
        .matches(CARACTERES_PERMITIDOS_REGEX, CARACTERES_PERMITIDOS_MSG)
        .required(mensajeRequerido);

/** Las reglas que comparten las cinco entidades: identificacion + domicilio. */
export const reglasEstablecimiento = () => ({
    cod: identificacion(LARGOS.cod, "Debe ingresar un Código"),
    name: identificacion(LARGOS.name, "Debe ingresar un nombre"),
    ...reglasUbicacion(),
});

/** Valores iniciales de un alta: todo vacio y el mapa en el centro por defecto. */
export const valoresVacios = () => ({
    cod: "",
    name: "",
    ...valoresUbicacionVacios(),
});

/**
 * Nombre y codigo, que son de las cinco entidades.
 *
 * El nombre va primero y ancho, y el codigo angosto al lado, para que la primera fila caiga
 * en la misma division que la segunda: nombre sobre calle y codigo sobre numero. Con el codigo
 * adelante las dos filas se cortaban en lugares distintos y se leia como desalineado.
 *
 * Van como componente y no inline en el JSX del bloque porque hospitales los intercala con los
 * suyos —Nombre, Nivel, Zona...— y necesita ponerlos los mismos en otro orden.
 */
export function CamposIdentificacion({ anchoName = estilos.col8, anchoCod = estilos.col4 }) {
    return (
        <>
            <CampoTexto
                className={anchoName}
                name="name"
                labelText="Nombre"
                placeholder="Nombre"
                inputProps={{ maxLength: LARGOS.name }}
            />
            <CampoTexto
                className={anchoCod}
                name="cod"
                labelText="Código"
                placeholder="Código"
                inputProps={{ maxLength: LARGOS.cod }}
            />
        </>
    );
}

/**
 * @param initialValues valores del formulario. En la modificacion tienen que venir ya cargados.
 * @param onSubmit      recibe los values del formulario con la coordenada del mapa mergeada.
 * @param botonTexto    texto del boton de submit.
 * @param esAlta        marca que el formulario es un alta: arranca sin coordenada elegida, asi
 *                      que no deja guardar hasta que el usuario ubique el establecimiento.
 * @param camposExtra   campos propios de la entidad. Si no se pasa, van codigo y nombre solos,
 *                      que es lo que necesitan cuatro de las cinco.
 * @param reglasExtra   las reglas de Yup de esos campos, para sumar al schema comun.
 * @param columnas      anchos de los campos del domicilio, si la entidad necesita otros.
 * @param mapaAncho     reparte 40/60 a favor del mapa y pone los campos de a uno por fila. Es
 *                      el default porque son ocho de los diez modales: solo hospitales, con
 *                      sus diez campos, necesita las mitades.
 * @param conPermisos   suma los dos checkbox de permisos de la reparticion al pie del
 *                      formulario, y con ellos sus valores por defecto.
 *
 *                      SOLO CAPS, PUESTOS Y POSTAS. En hospitales y URESA el valor lo
 *                      decide el back validando el tipo, asi que mostrar los checkbox ahi
 *                      seria ofrecer una decision que despues no se respeta.
 *
 *                      Se lleva puesto el reparto del domicilio: Calle y Numero pasan a
 *                      compartir renglon, y el que se libera es el que ocupan los checkbox.
 *                      Asi el formulario mide lo mismo que antes y el mapa no se achica.
 */
export default function EstablecimientoFormulario({
    initialValues,
    onSubmit,
    botonTexto,
    inRequest,
    closeModal,
    esAlta = false,
    camposExtra = null,
    reglasExtra = {},
    columnas = null,
    mapaAncho = true,
    conPermisos = false,
}) {
    const credentials = useRecoilValue(userCredentials);
    const formikRef = useRef();

    /**
     * El numero, de vuelta a vacio cuando el registro guardado dice "S/N".
     *
     * "S/N" no es un numero que alguien tipeo, es la marca de que la calle no tiene altura. Si
     * entrara al campo tal cual, la barra —que no esta entre los caracteres permitidos— haria
     * fallar la validacion al guardar, y el usuario no podria arreglarlo porque tampoco puede
     * escribirla. Vacio, el campo dice lo mismo (el placeholder es "S/N") y al enviar se vuelve
     * a plantar.
     *
     * Va aca y no en los cinco modales de modificacion: es el unico lugar por el que pasan
     * todos, y asi la regla vive junto a la que planta el valor al enviar.
     */
    const valoresIniciales = {
        // Los permisos van primero para que sean el PISO: en un alta no vienen y quedan en
        // "N", y en una modificacion los que trae `initialValues` los pisan.
        ...(conPermisos ? PERMISOS_INICIALES : {}),
        ...initialValues,
        numero: esSinNumero(initialValues?.numero) ? "" : initialValues?.numero,
    };

    // Con el numero ya normalizado: si fuera "S/N", el geocoding buscaria una altura que no
    // existe en vez de caer a la calle.
    const ubicacion = useUbicacion({
        esAlta,
        initialValues: valoresIniciales,
        formikRef,
    });

    const validationSchema = yup.object({
        ...reglasEstablecimiento(),
        ...reglasExtra,
    });

    /**
     * Freno de mano antes de mandar: sin coordenada elegida, no se guarda.
     *
     * La coordenada no esta en el schema de Yup porque no se tipea, sale del mapa. Por eso el
     * chequeo va aca, y el aviso aparece al lado del mapa en vez de en un snackbar: es donde
     * el usuario tiene que actuar.
     */
    const intentarEnviar = (values) => {
        if (!ubicacion.definida) {
            ubicacion.marcarFaltaUbicar();
            return;
        }
        // El punto lo manda el mapa, no el formulario: se mergea recien al enviar.
        onSubmit({
            ...values,
            // El back pide el numero si o si. Una calle sin altura se guarda con la marca
            // convenida, que el usuario no puede escribir a mano.
            numero: String(values.numero ?? "").trim() || SIN_NUMERO,
            ...ubicacion.coordenadas,
        });
    };

    return (
        <Formik
            innerRef={formikRef}
            initialValues={valoresIniciales}
            validationSchema={validationSchema}
            onSubmit={(values) => intentarEnviar(values)}
        >
            <Form noValidate>
                <BloqueUbicacion
                    credentials={credentials}
                    ubicacion={ubicacion}
                    columnas={
                        columnas ??
                        (conPermisos && mapaAncho
                            ? ANCHOS_DOMICILIO_ANGOSTO_COMPACTO
                            : null)
                    }
                    mapaAncho={mapaAncho}
                    camposAntes={camposExtra ?? <CamposIdentificacion />}
                    camposDespues={
                        conPermisos ? (
                            <div className={estilos.col12}>
                                {/* `compacto` porque esta columna mide ~330px: con las
                                    etiquetas largas las dos casillas no entran en un
                                    renglón y se apilan. */}
                                <PermisosDeReparticion compacto />
                            </div>
                        ) : null
                    }
                />

                <FormButton
                    inRequest={inRequest}
                    newFormData={{ button: botonTexto }}
                    onCloseModal={closeModal}
                />
            </Form>
        </Formik>
    );
}
